import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GenerateVideoPlaceholderRequestBody, MediaQualityProperties, OptimizeMediaBody } from '@motes-sdk/api-models/media';
import { AllowedMediaMap, ContainerOptions, ImageFormat, ImageFormatMap, Media, MediaFile, MediaFileType, MediaType, MotesPaths } from '@motes-sdk/models';
import { MediaService, RestService, ToastService, ToastType } from '@motes-sdk/providers';
import { Subject, firstValueFrom } from 'rxjs';
import { first } from 'rxjs/operators';
import { UploadCropperComponent } from '../upload-cropper/upload-cropper.component';
import { UploadManagerService } from '../upload-manager/upload-manager.service';

export enum MediaUploadEventType {
    UPLOAD_ACCEPTED = "UPLOAD_ACCEPTED",
    DELETE_OLD_STARTED = "DELETE_OLD_STARTED",
    OLD_FILE_DELETED = "OLD_FILE_DELETED",
    IMAGE_UPLOAD_STARTED = "IMAGE_UPLOAD_STARTED",
    MEDIA_UPLOAD_STARTED = "MEDIA_UPLOAD_STARTED",
    MEDIA_UPLOAD_DONE = "MEDIA_UPLOAD_DONE",
    UPDATE_MEDIA_TYPE = "UPDATE_MEDIA_TYPE",
    MEDIA_CROPPING_DONE = "MEDIA_CROPPING_DONE",
}

export interface MediaUploadEvent {
    type: MediaUploadEventType,
    media: Media,
    file?: MediaFile;
}

@Injectable({ 'providedIn': 'root' })
export class UploadMediaService {
    private uploadEventSubject: Subject<MediaUploadEvent> = new Subject();

    constructor(
        private uploadManagerService: UploadManagerService,
        private mediaService: MediaService,
        private httpClient: HttpClient,
        private toastService: ToastService,
        private restService: RestService) {
    }

    static readonly DefaultImageFormat: ImageFormatMap = {
        'hd': new ImageFormat(MediaFileType.HD, true, 16 / 9, 1280, true),
        'preview': new ImageFormat(MediaFileType.PREVIEW, true, 16 / 9, 512, true, false, MediaFileType.HD),
        'square': new ImageFormat(MediaFileType.SQUARE, true, 1 / 1, 800, true, false, MediaFileType.HD),
        'thumb': new ImageFormat(MediaFileType.THUMB, true, 1 / 1, 256, true, false, MediaFileType.HD),
    };
    public watchUploadEvents() {
        return this.uploadEventSubject.asObservable();
    }

    /**
     * Upload a media object and the associated files to firebase database / storage
     * @param media Media of file to be uploaded
     * @param fileEv Media file to be uploaded
     * @param cropper Image cropper associated with a given image file
     * @param format Map of formats that image file should be cropped/scaled in
     * @param allowedMediaTypes Map of media types handled by the upload component that invoked this upload
     * @param containerOptions Options related to the container (info about media storage etc.), containing the media object
     */
    public async uploadMedia(
        media: Media,
        fileEv: MediaFile,
        cropper: UploadCropperComponent = null,
        format: ImageFormatMap,
        allowedMediaTypes: AllowedMediaMap,
        containerOptions: ContainerOptions,
        setVideoImagePlaceholder: boolean = false,
    ) {
        this.sendUploadEvent(MediaUploadEventType.UPLOAD_ACCEPTED, media);

        // Change the media type to reflect the newly uploaded file
        let convertBackToImage = (fileEv.mediaType == MediaType.IMAGE && !media['sound'].url && !media['ambisonic'].url && !media['video'].url);
        if(fileEv.mediaType == MediaType.SOUND || fileEv.mediaType == MediaType.AMBISONIC || fileEv.mediaType == MediaType.VIDEO || media.type == MediaType.UNDEFINED || convertBackToImage) {
            media.type = fileEv.mediaType;
        }

        // Delete old files before new upload
        await this.deleteOldFiles(fileEv, media);

        // Get reference to upload file from media object
        let file = (fileEv.mediaType == MediaType.IMAGE) ? media.original : media[fileEv.type];

        // Upload images
        if(fileEv.mediaType == MediaType.IMAGE) {
            // Set url and blob of original (to be shown while deleting)
            media.original.blob = fileEv.blob;
            media.original.url = fileEv.url;

            if(!media.original.blob) {
                console.warn("Media has no origin blob, cancel upload of file in media: " + media.key);
                return;
            }

            // Clear current cropping of all files before creating new auto cropping
            media.files.forEach(file => file.cropping = null);
            // Clear rotation on all files
            media.files.forEach(file => file.rotation = 0);

            // Do client side cropping if there is a cropper available
            if(cropper) {
                // Create shortcut cropper function
                let cropImage = (type: MediaFileType) => this.cropImage(media, format, cropper, type, false);
                // Crop all images client side (find cropping area and use this cropping temporary until server side cropping is done)
                // OBS: The crop image function updates the cropping coordinates of the cropped file and the linked file if any
                await cropImage(MediaFileType.HD);
                await cropImage(MediaFileType.PREVIEW);
                await cropImage(MediaFileType.SQUARE);
                await cropImage(MediaFileType.THUMB);
            }

            this.sendUploadEvent(MediaUploadEventType.IMAGE_UPLOAD_STARTED, media, file);
        }
        else {
            // Update media with urls from event
            file.type = fileEv.type;
            file.blob = fileEv.blob;
            file.url = fileEv.url;
            this.sendUploadEvent(MediaUploadEventType.MEDIA_UPLOAD_STARTED, media, file);
        }

        // Start upload
        this.uploadManagerService.startUpload(file, media.key);

        // Await media upload completes
        await firstValueFrom(this.uploadManagerService.watchUploadGroupResult(media.key));

        this.sendUploadEvent(MediaUploadEventType.MEDIA_UPLOAD_DONE, media, file);
        // Optimize media if needed
        this.optimizeMedia(media, file, allowedMediaTypes, containerOptions, format, fileEv.mediaType, setVideoImagePlaceholder);
    }

    public async optimizeMedia(
        media: Media,
        file: MediaFile,
        allowedMediaTypes: AllowedMediaMap,
        containerOptions: ContainerOptions,
        format: ImageFormatMap,
        mediaType: MediaType,
        setVideoImagePlaceholder: boolean = true,
    ) {
        if(!media || !containerOptions) {
            // Stringify input options to capture current state
            const mediaStr = media?.toJson() || 'No media';
            const optionsStr = containerOptions ? JSON.stringify(containerOptions) : 'No container options';
            // Print error and abort optimization
            console.error("No media or container options input specified. Abort optimize media: ", mediaStr, optionsStr);
            return;
        }
        this.updateLinkedCropping(media, format);

        // Update origin url in media database if the file should be handled by this component
        await this.mediaService.updateMedia(media, containerOptions);

        // Check if the files uploaded is the origin image and that this is allowed/should be handled in this component
        if(((file.type == MediaFileType.ORIGINAL || file.mediaType == MediaType.SOUND) && allowedMediaTypes[file.mediaType])) {
            // Start server side cropping and optimization of images
            await this.requestOptimizeMedia(media, containerOptions, format, mediaType);
        }

        // Generate image placeholder if media is video
        if(setVideoImagePlaceholder && file.type == MediaFileType.VIDEO) {
            await this.generateScreenshot(media, containerOptions, 0);
        }

        // Always send upload cropping done for consistency (even uploaded type was video or sound)
        this.sendUploadEvent(MediaUploadEventType.MEDIA_CROPPING_DONE, media, file);
    }

    private getResolutionFromImageFormat(imageFormat: ImageFormat): string {
        const height = imageFormat.resizeToWidth / imageFormat.aspectRatio;
        return `${imageFormat.resizeToWidth}x${height}`;
    }

    private async requestOptimizeMedia(media: Media, containerOptions: ContainerOptions, imageFormats: ImageFormatMap, mediaType: MediaType) {
        const optimizeRequestBody: OptimizeMediaBody = {
            containerOptions: containerOptions,
            toOptimize: {
                image: mediaType == MediaType.IMAGE,
                audio: mediaType == MediaType.SOUND,
            }
        };

        const formats = Object.entries(imageFormats);
        // Add image quality for each media type to body of optimize re
        for(const [mediaType, imageFormat] of formats) {
            const qualityProperties: MediaQualityProperties = {
                quality: 75,
                size: this.getResolutionFromImageFormat(imageFormat),
            };
            optimizeRequestBody[mediaType] = qualityProperties;
        }

        const authOptions = await this.restService.getAuthOptions();
        const optimizeEndpoint = `${MotesPaths.MOTES_REST}/v1/media/${media.key}/optimize`;
        const fetchObservable = this.httpClient.post(optimizeEndpoint, optimizeRequestBody, { headers: authOptions });

        await firstValueFrom(fetchObservable).catch(async (err) => {
            console.error(err);
            this.toastService.addToast('Something went wrong while uploading media, please try again', ToastType.ERR);
        });
    }

    public async generateScreenshot(media: Media, containerOptions: ContainerOptions, time: number = 0) {
        if(!media || !containerOptions) {
            // Stringify input options to capture current state
            const mediaStr = media?.toJson() || 'No media';
            const optionsStr = containerOptions ? JSON.stringify(containerOptions) : 'No container options';
            // Print error and abort optimization
            console.error("No media or container options input specified. Abort optimize media: ", mediaStr, optionsStr);
            return;
        }

        const imageFiles = media.files.filter(file => file.url && file.mediaType == MediaType.IMAGE);
        await this.mediaService.deleteFiles(imageFiles);
        await this.mediaService.updateMedia(media, containerOptions);
        await this.requestGenerateScreenshot(media, time, containerOptions);
    }

    private async requestGenerateScreenshot(media: Media, time: number, containerOptions: ContainerOptions) {
        const generateScreenBody: GenerateVideoPlaceholderRequestBody = { time, containerOptions };
        const authOptions = await this.restService.getAuthOptions();
        const fetchObservable = this.httpClient.post(
            MotesPaths.GENERATE_SCREENSHOT_ENDPOINT(media.key),
            generateScreenBody,
            { headers: authOptions }
        );

        await fetchObservable.toPromise().catch(async (err) => {
            console.error(err);
            this.toastService.addToast('Something went wrong while generating screenshot, please try again', ToastType.ERR);
        });
    }

    private updateLinkedCropping(media: Media, imageFormats: ImageFormatMap) {
        for(let file of media.files) {
            // Find linked type of file
            const linkedFileType = imageFormats[file.type]?.linked;
            const linkedFile = media[linkedFileType];
            // Update cropping from linked
            if(linkedFile?.cropping) file.cropping = linkedFile.cropping;
        }
    }

    private async deleteOldFiles(file: MediaFile, media: Media) {
        this.sendUploadEvent(MediaUploadEventType.DELETE_OLD_STARTED, media);
        // Delete all old files of this type, but don't cleat original url
        await this.deleteFiles(media[file.type], media, false);

        // Ensure video/sound is deleted on sound/video upload
        if(file.type == MediaFileType.SOUND) await this.deleteFiles(media[MediaFileType.VIDEO], media, false);
        if(file.type == MediaFileType.VIDEO) await this.deleteFiles(media[MediaFileType.SOUND], media, false);

        this.sendUploadEvent(MediaUploadEventType.OLD_FILE_DELETED, media, file);
        // Obs, this time out makes sure that angular will remove the current video/audio source for a short while
        // which is needed in order to update the dom and load a new audio/video source the second time
        await new Promise<void>((resolve) => setTimeout(() => { resolve(); }, 0));
        // Update storage paths after delete (to get rid of legacy paths)
        // media.initStoragePaths(this.storagePath, true);
    }

    public async deleteFiles(file: MediaFile, media: Media, clearOriginal: boolean = true) {
        if(!file || !media) return;
        // Batch delete all images or simply delete one file
        let deleteFiles = (file.mediaType == MediaType.IMAGE) ? media.files.filter(file => file.mediaType == MediaType.IMAGE) : [file];

        // Delete all file and clear urls (delete original but don't clear any temporary urls if not stated)
        await this.mediaService.deleteFiles(deleteFiles, clearOriginal);
        // Clear cropping and rotation data of all files if any
        deleteFiles.forEach(file => {
            media[file.type].cropping = null;
            media[file.type].rotation = 0;
            media[file.type].url = "";
        });
    }

    /**
    * Update the media type (typically after a delete of a file in the media object)
    */
    public updateMediaType(media: Media): Media {
        // Prioritized list of media types (to be used if the media object contains this type of file)
        const priorityList = [
            media.type, // Prefer current type if for example both video and sound exists and use choose the right one
            MediaType.VIDEO,
            MediaType.AMBISONIC,
            MediaType.SOUND,
            MediaType.IMAGE
        ];

        for(let type of priorityList) {
            let url = this.getMediaUrl(media, type);
            // If url does not exists, try next type
            if(!url) continue;
            // Select type if it exists
            media.type = type;
            return media;
        }
        // Fallback to image type
        media.type = MediaType.IMAGE;
    }

    private getMediaUrl(media: Media, type: MediaType): string {
        switch(type) {
            case MediaType.YOUTUBE_VIDEO:
            case MediaType.FILMCENTRALEN_VIDEO:
            case MediaType.VIDEO:
                return media.video ? media.video.url : "";

            case MediaType.AMBISONIC:
                return media.ambisonic ? media.ambisonic.url : "";

            case MediaType.SOUND:
                return media.sound ? media.sound.url : "";

            case MediaType.IMAGE:
            default:
                return media.hd.url || media.square.url || media.thumb.url;
        }
    }

    public async cropImage(media: Media, imageFormats: ImageFormatMap, cropper: UploadCropperComponent, type: MediaFileType, manualCropping: boolean = false, roundCropper: boolean = false): Promise<MediaFile | null> {
        // Get the file to be cropped
        let file: MediaFile = media[type];
        let format = imageFormats[type];

        if(!format) return null;
        // Await file cropper to crop it
        let croppedEv = await cropper.cropImage(file, media.original, format, manualCropping, roundCropper);
        // If cropping was canceled
        if(!croppedEv) return null;
        [file.blob, file.url, file.cropping, file.rotation] = croppedEv;
        // OBS: Linked files are not automatically cropped - this is done server side
        return file;
    }

    public sendUpdateMediaTypeEvent(media: Media) {
        this.sendUploadEvent(MediaUploadEventType.UPDATE_MEDIA_TYPE, media);
    }

    public sendUploadEvent(type: MediaUploadEventType, media: Media, file?: MediaFile) {
        let ev: MediaUploadEvent = {
            type: type,
            media: media,
            file: file || null
        };
        this.uploadEventSubject.next(ev);
    }
}
